import Modal from "./Modal";
import TwoColumnDiv from "./TwoColumnDiv";
import React, { useRef, useState } from "react";
import Button from "../button/Button";
import HorizontalRule from "./HorizontalRule";
import { FormikValues, useField, useFormikContext } from "formik";
import { ValidatedAddress } from "../../generated/graphql";
import { useApolloClient } from "@apollo/client";
import { VALIDATE_ADDRESS_QUERY } from "../../Queries";

export type AddressModalProps = {
    streetAddressField: string,
    cityField: string,
    stateField: string,
    zipCodeField: string,
    children?: React.ReactNode;
}

const AddressModal = (props: AddressModalProps) => {
    const { setFieldValue, validateForm, submitForm, values } = useFormikContext<FormikValues>();
    const [correctedAddress, setCorrectedAddress] = useState(null as any as ValidatedAddress);
    const modalRef = useRef() as React.MutableRefObject<any>;
    const apolloClient = useApolloClient();

    const { streetAddressField, cityField, stateField, zipCodeField } = props;
    const enteredAddress = {
        streetAddress: values[streetAddressField],
        city: values[cityField],
        state: values[stateField],
        zipCode: values[zipCodeField]
    } as ValidatedAddress;

    const useEnteredAddress = values.useEnteredAddress;

    useField({
        name: 'useEnteredAddress',
        validate: async () => {
            const modalRefCurrent = modalRef.current;
            if(!modalRefCurrent || modalRefCurrent.closest('.invisible')) {
                return ''
            }
            const correctedAddress = await findCorrectedAddressFor(enteredAddress, useEnteredAddress);
            setCorrectedAddress(correctedAddress);
            if(correctedAddress) {
                return {
                    hidden: true,
                    message: correctedAddress
                } as any
            }
            return '';
        }
    });

    return <div ref={modalRef}>
        {(correctedAddress && !useEnteredAddress)
            && <Modal id="address-validation"
                      headerText="Address Verification"
                      modalContent={
                          <div>
                              We are unable to verify your address as entered, but found a close match. Please confirm the correct address.
                              <HorizontalRule/>
                              <h5>Entered Address:</h5>
                              <TwoColumnDiv leftContent={<>
                                  <div>{enteredAddress.streetAddress}</div>
                                  <div>{enteredAddress.city + ', ' + enteredAddress.state + ' ' + enteredAddress.zipCode}</div>
                              </>} rightContent={<Button type="submit"
                                                         buttonContent="Select Address"
                                                         onClick={async () => {
                                                             await setFieldValue('useEnteredAddress', true, false);
                                                             await validateAndSubmitForm();
                                                         }}/>}>
                              </TwoColumnDiv>
                              <HorizontalRule/>
                              <h5>Suggested Address:</h5>
                              <TwoColumnDiv leftContent={<>
                                  <div>{correctedAddress.streetAddress}</div>
                                  <div>{correctedAddress.city + ', ' + correctedAddress.state + " " + correctedAddress.zipCode}</div>
                              </>} rightContent={<Button type="submit"
                                                         buttonContent="Select Address"
                                                         onClick={async () => {
                                                             await Promise.all([
                                                                 setFieldValue(streetAddressField, correctedAddress.streetAddress, false),
                                                                 setFieldValue(cityField, correctedAddress.city, false),
                                                                 setFieldValue(stateField, correctedAddress.state, false),
                                                                 setFieldValue(zipCodeField, correctedAddress.zipCode, false)
                                                             ]);
                                                             await validateAndSubmitForm();
                                                         }}/>}>
                              </TwoColumnDiv>
                          </div>}
                      closeable={false}/>}
    </div>;

    async function findCorrectedAddressFor(enteredAddress: ValidatedAddress, useEnteredAddress: boolean) {
        const { streetAddress, city, state, zipCode } = enteredAddress
        if (!streetAddress || !city || !state || 5 !== zipCode?.length || useEnteredAddress) {
            return;
        }
        try {
            const validatedAddress = await apolloClient.query({
                query: VALIDATE_ADDRESS_QUERY,
                variables: {
                    validateAddressInput: {
                        streetAddress,
                        city,
                        state,
                        zipCode
                    }
                }
            });
            const uspsAddress = validatedAddress.data?.validateAddress
            if (uspsAddress
                && (uspsAddress.streetAddress.toUpperCase() !== streetAddress.toUpperCase()
                    || uspsAddress.city.toUpperCase() !== city.toUpperCase()
                    || uspsAddress.state.toUpperCase() !== state.toUpperCase()
                    || uspsAddress.zipCode.toUpperCase() !== zipCode.toUpperCase())) {
                return uspsAddress;
            }
        } catch(e) {
            console.log(e)
            return null;
        }
    }

    async function validateAndSubmitForm() {
        await validateForm();
        await submitForm();
    }
}

export default AddressModal;
